import React, { useRef, useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from '../i18n/useTranslation';
import { useQuestionnaire } from '../context/QuestionnaireContext';
import { uploadApi } from '../services/api';
import * as blazeface from '@tensorflow-models/blazeface';
import '@tensorflow/tfjs';
import { extractFaceHighRes } from '../utils/blazeFaceCrop';
import './CameraPage.css';

const CameraPage: React.FC = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { answers } = useQuestionnaire();
  const videoRef = useRef<HTMLVideoElement>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const [isCameraActive, setIsCameraActive] = useState(false);
  const [faceDetected, setFaceDetected] = useState(false);
  const [faceClear, setFaceClear] = useState(false);
  const [model, setModel] = useState<blazeface.BlazeFaceModel | null>(null);
  const [stream, setStream] = useState<MediaStream | null>(null);
  const [capturedImage, setCapturedImage] = useState<string | null>(null);
  const [isUploading, setIsUploading] = useState(false);
  const [uploadError, setUploadError] = useState<string | null>(null);
  const [uploadProgress, setUploadProgress] = useState<number>(0);
  const animationFrameRef = useRef<number | null>(null);
  const faceBoxRef = useRef<{ x: number; y: number; width: number; height: number } | null>(null);

  // Check GDPR consent on mount - redirect if not given
  useEffect(() => {
    const gdprConsent = localStorage.getItem('gdpr-consent-given');
    if (!gdprConsent) {
      // Redirect to GDPR consent page if consent not given
      navigate('/gdpr-consent');
      return;
    }
    // If consent already given, start camera immediately
    console.log('GDPR consent found, starting camera...');
    startCamera().catch((error) => {
      console.error('Failed to start camera:', error);
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Load face detection model
  useEffect(() => {
    const loadModel = async () => {
      try {
        const loadedModel = await blazeface.load();
        setModel(loadedModel);
      } catch (error) {
        console.error('Error loading face detection model:', error);
      }
    };
    loadModel();
  }, []);

  // Start camera
  const startCamera = useCallback(async () => {
    try {
      console.log('Requesting camera access...');
      let mediaStream: MediaStream;

      const highResConstraints: MediaStreamConstraints = {
        video: {
          facingMode: 'user',
          // Try to get a reasonably high, non-optimized stream (especially on iPhone).
          width: { ideal: 1280, max: 1920 },
          height: { ideal: 960, max: 1080 },
          frameRate: { ideal: 30 },
        },
      };

      try {
        mediaStream = await navigator.mediaDevices.getUserMedia(highResConstraints);
      } catch (err: any) {
        if (err?.name === 'OverconstrainedError' || err?.name === 'ConstraintNotSatisfiedError') {
          console.warn('High-res constraints not satisfied, falling back to simpler constraints:', err);
          mediaStream = await navigator.mediaDevices.getUserMedia({
            video: { facingMode: 'user' },
          });
        } else {
          throw err;
        }
      }
      
      console.log('Camera stream obtained:', mediaStream);
      console.log('Video tracks:', mediaStream.getVideoTracks());
      
      if (!videoRef.current) {
        console.error('Video ref is not available');
        return;
      }

      // Set stream first
      setStream(mediaStream);
      videoRef.current.srcObject = mediaStream;
      
      // Try to play immediately
      try {
        await videoRef.current.play();
        console.log('Video play() called successfully');
        setIsCameraActive(true);
      } catch (playError) {
        console.error('Error playing video immediately:', playError);
        // Video might need to wait for metadata
      }
    } catch (error: any) {
      console.error('Error accessing camera:', error);
      if (error.name === 'NotAllowedError' || error.name === 'PermissionDeniedError') {
        alert('Camera permission denied. Please allow camera access and refresh the page.');
      } else if (error.name === 'NotFoundError' || error.name === 'DevicesNotFoundError') {
        alert('No camera found. Please connect a camera and refresh the page.');
      } else {
        alert('Unable to access camera. Please check permissions and try again.');
      }
    }
  }, []);

  // Stop camera
  const stopCamera = useCallback(() => {
    if (stream) {
      stream.getTracks().forEach(track => track.stop());
      setStream(null);
    }
    if (videoRef.current) {
      videoRef.current.srcObject = null;
    }
    setIsCameraActive(false);
    if (animationFrameRef.current) {
      cancelAnimationFrame(animationFrameRef.current);
    }
  }, [stream]);

  // Detect faces
  const detectFace = useCallback(async () => {
    if (!videoRef.current || !model || !canvasRef.current) return;

    const video = videoRef.current;
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');

    if (!ctx) return;

    // Check if video has valid dimensions
    if (video.videoWidth === 0 || video.videoHeight === 0) {
      animationFrameRef.current = requestAnimationFrame(detectFace);
      return;
    }

    // Set canvas to match video display size (not source size)
    const displayWidth = video.offsetWidth || video.clientWidth;
    const displayHeight = video.offsetHeight || video.clientHeight;
    
    // Use video source dimensions for face detection, but scale drawing to display
    const scaleX = displayWidth / video.videoWidth;
    const scaleY = displayHeight / video.videoHeight;
    
    canvas.width = displayWidth;
    canvas.height = displayHeight;

    try {
      // Clear canvas
      ctx.clearRect(0, 0, canvas.width, canvas.height);

      // Draw static guide circle (always visible)
      const guideCenterX = canvas.width / 2;
      const guideCenterY = canvas.height / 2;
      const guideRadius = Math.min(canvas.width, canvas.height) * 0.35; // 35% of smaller dimension

      // Draw guide circle with dashed line
      ctx.strokeStyle = '#FFFFFF';
      ctx.lineWidth = 3;
      ctx.setLineDash([10, 5]); // Dashed line pattern
      ctx.beginPath();
      ctx.arc(guideCenterX, guideCenterY, guideRadius, 0, 2 * Math.PI);
      ctx.stroke();

      // Draw guide crosshair marks at cardinal points
      ctx.strokeStyle = '#FFFFFF';
      ctx.lineWidth = 2;
      ctx.setLineDash([]); // Solid lines for crosshair
      
      const crosshairLength = 15;
      
      // Top crosshair
      ctx.beginPath();
      ctx.moveTo(guideCenterX, guideCenterY - guideRadius - crosshairLength);
      ctx.lineTo(guideCenterX, guideCenterY - guideRadius + crosshairLength);
      ctx.stroke();
      
      // Bottom crosshair
      ctx.beginPath();
      ctx.moveTo(guideCenterX, guideCenterY + guideRadius - crosshairLength);
      ctx.lineTo(guideCenterX, guideCenterY + guideRadius + crosshairLength);
      ctx.stroke();
      
      // Left crosshair
      ctx.beginPath();
      ctx.moveTo(guideCenterX - guideRadius - crosshairLength, guideCenterY);
      ctx.lineTo(guideCenterX - guideRadius + crosshairLength, guideCenterY);
      ctx.stroke();
      
      // Right crosshair
      ctx.beginPath();
      ctx.moveTo(guideCenterX + guideRadius - crosshairLength, guideCenterY);
      ctx.lineTo(guideCenterX + guideRadius + crosshairLength, guideCenterY);
      ctx.stroke();

      // Now detect faces and draw dynamic overlay if face is detected
      const predictions = await model.estimateFaces(video, false);

      if (predictions.length > 0) {
        console.log('Face detected! Predictions:', predictions.length);
        const prediction = predictions[0];
        const start = prediction.topLeft as [number, number];
        const end = prediction.bottomRight as [number, number];
        const size = [end[0] - start[0], end[1] - start[1]];

        // Calculate center and radius for circle (in video source coordinates)
        const centerX = start[0] + size[0] / 2;
        const centerY = start[1] + size[1] / 2;
        const radius = Math.max(size[0], size[1]) / 2;

        // Check if face is clear (good size and position) BEFORE drawing
        const faceArea = size[0] * size[1];
        const videoArea = video.videoWidth * video.videoHeight;
        const faceRatio = faceArea / videoArea;
        
        // Face should be reasonably centered and within a sensible size range.
        // Be a bit more lenient so users don't have to be pixel-perfect.
        const isCentered = Math.abs(centerX - video.videoWidth / 2) < video.videoWidth * 0.25;
        const isGoodSize = faceRatio > 0.08 && faceRatio < 0.6;
        const isFaceClear = isCentered && isGoodSize;
        
        setFaceDetected(true);
        setFaceClear(isFaceClear);

        // Persist last good face box (in video source coordinates) for precise cropping on capture
        if (isFaceClear) {
          faceBoxRef.current = {
            x: start[0],
            y: start[1],
            width: size[0],
            height: size[1],
          };
        }

        // Scale coordinates to match display size and mirror to match video
        const scaledX = centerX * scaleX;
        const scaledY = centerY * scaleY;
        const scaledRadius = radius * Math.max(scaleX, scaleY);
        const mirroredX = canvas.width - scaledX;
        
        // Draw dynamic face detection circle overlay (on top of guide circle)
        ctx.strokeStyle = isFaceClear ? '#4CAF50' : '#FFC107';
        ctx.lineWidth = 3;
        ctx.setLineDash([10, 5]); // Dashed line pattern
        ctx.beginPath();
        ctx.arc(mirroredX, scaledY, scaledRadius, 0, 2 * Math.PI);
        ctx.stroke();

        // Draw dynamic crosshair at face position
        ctx.strokeStyle = isFaceClear ? '#4CAF50' : '#FFC107';
        ctx.lineWidth = 2;
        ctx.setLineDash([]); // Solid lines for crosshair
        
        const crosshairLength = 15;
        
        // Top crosshair
        ctx.beginPath();
        ctx.moveTo(mirroredX, scaledY - scaledRadius - crosshairLength);
        ctx.lineTo(mirroredX, scaledY - scaledRadius + crosshairLength);
        ctx.stroke();
        
        // Bottom crosshair
        ctx.beginPath();
        ctx.moveTo(mirroredX, scaledY + scaledRadius - crosshairLength);
        ctx.lineTo(mirroredX, scaledY + scaledRadius + crosshairLength);
        ctx.stroke();
        
        // Left crosshair
        ctx.beginPath();
        ctx.moveTo(mirroredX - scaledRadius - crosshairLength, scaledY);
        ctx.lineTo(mirroredX - scaledRadius + crosshairLength, scaledY);
        ctx.stroke();
        
        // Right crosshair
        ctx.beginPath();
        ctx.moveTo(mirroredX + scaledRadius - crosshairLength, scaledY);
        ctx.lineTo(mirroredX + scaledRadius + crosshairLength, scaledY);
        ctx.stroke();
      } else {
        setFaceDetected(false);
        setFaceClear(false);
      }
    } catch (error) {
      console.error('Error detecting face:', error);
    }

    animationFrameRef.current = requestAnimationFrame(detectFace);
  }, [model]);

  // Ensure video plays when stream is set
  useEffect(() => {
    if (videoRef.current && stream) {
      const video = videoRef.current;
      
      // Ensure srcObject is set
      if (video.srcObject !== stream) {
        video.srcObject = stream;
      }
      
      const playVideo = async () => {
        try {
          if (video.paused) {
            await video.play();
          }
          // Mark as active immediately when playing starts
          setIsCameraActive(true);
          console.log('Video playing, dimensions:', video.videoWidth, 'x', video.videoHeight);
          
          // Wait for video to have valid dimensions for face detection
          const checkDimensions = () => {
            if (video.videoWidth > 0 && video.videoHeight > 0) {
              console.log('Video has valid dimensions:', video.videoWidth, 'x', video.videoHeight);
            } else {
              // Retry after a short delay
              setTimeout(checkDimensions, 100);
            }
          };
          checkDimensions();
        } catch (error) {
          console.error('Error playing video:', error);
        }
      };
      
      // Set up event listeners
      const handleLoadedMetadata = () => {
        console.log('Video metadata loaded');
        playVideo();
      };
      
      const handleCanPlay = () => {
        console.log('Video can play');
        if (!isCameraActive) {
          playVideo();
        }
      };
      
      const handlePlaying = () => {
        console.log('Video is playing');
        setIsCameraActive(true);
      };
      
      video.addEventListener('loadedmetadata', handleLoadedMetadata);
      video.addEventListener('canplay', handleCanPlay);
      video.addEventListener('playing', handlePlaying);
      
      // Try to play immediately if ready
      if (video.readyState >= 2) {
        playVideo();
      } else {
        // Also try after a short delay
        setTimeout(() => {
          if (video.readyState >= 2) {
            playVideo();
          }
        }, 100);
      }
      
      return () => {
        video.removeEventListener('loadedmetadata', handleLoadedMetadata);
        video.removeEventListener('canplay', handleCanPlay);
        video.removeEventListener('playing', handlePlaying);
      };
    }
  }, [stream, isCameraActive]);

  // Start detection loop when camera is active
  useEffect(() => {
    if (isCameraActive && model && videoRef.current) {
      console.log('Starting face detection loop');
      detectFace();
    } else {
      console.log('Face detection not started:', { isCameraActive, hasModel: !!model, hasVideo: !!videoRef.current });
    }

    return () => {
      if (animationFrameRef.current) {
        cancelAnimationFrame(animationFrameRef.current);
      }
    };
  }, [isCameraActive, model, detectFace]);


  // Capture photo – grab full frame, then refine to high‑res face crop with BlazeFace for preview
  const capturePhoto = async () => {
    if (!videoRef.current || !canvasRef.current) return;

    const video = videoRef.current;
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');

    if (!ctx) return;

    // Use full video source dimensions for the initial frame
    canvas.width = video.videoWidth;
    canvas.height = video.videoHeight;
    ctx.drawImage(video, 0, 0, canvas.width, canvas.height);

    const fullFrameDataUrl = canvas.toDataURL('image/jpeg', 0.98);

    // Refine to face-only using BlazeFace helper so preview matches what we send
    const faceOnlyDataUrl = model
      ? await extractFaceHighRes(fullFrameDataUrl, model)
      : fullFrameDataUrl;
    console.log('Capture preview (face-only) length:', faceOnlyDataUrl.length);
    console.log('Capture preview (face-only) preview:', faceOnlyDataUrl.substring(0, 120));

    setCapturedImage(faceOnlyDataUrl);
    stopCamera();
  };

  // Start face analysis
  const handleStartAnalysis = async () => {
    if (!capturedImage) return;

    // capturedImage is already face-only at this point
    console.log('Captured image being uploaded (dataUrl length):', capturedImage.length);
    console.log('Captured image dataUrl preview:', capturedImage);

    try {
      setIsUploading(true);
      setUploadError(null);
      setUploadProgress(0);

      // Upload face-only cropped selfie as 10KB chunks (sequential, 1 concurrent request)
      const uploadResult = await uploadApi.uploadSelfieChunked({
        dataUrl: capturedImage,
        chunkSizeBytes: 10 * 1024,
        concurrentRequests: 1,
        fileName: 'selfie.jpg',
        onProgress: (uploadedChunks, totalChunks) => {
          const percent = Math.round((uploadedChunks / totalChunks) * 100);
          setUploadProgress(percent);
        },
      });

      console.log('Questionnaire Answers:', answers);
      console.log('Selfie upload result:', uploadResult);

      const imagesResponse =
        uploadResult?.lastResponse ?? uploadResult?.results?.[uploadResult.totalChunks - 1];
      const analysisImages = imagesResponse?.Images || imagesResponse?.images || null;
      const analysisResults =
        imagesResponse?.AnalysisResults || imagesResponse?.analysisResults || null;

      // Stop camera before leaving this page so the browser no longer shows this tab using the camera
      stopCamera();

      // Navigate to results page with captured image, analysis overlay images and numeric results
      navigate('/results', {
        state: {
          capturedImage,
          uploadId: uploadResult.uploadId,
          analysisImages,
          analysisResults,
        },
      });
    } catch (err: any) {
      console.error('Selfie upload failed:', err);
      setUploadError(t('camera.uploadError'));
    } finally {
      setIsUploading(false);
    }
  };

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      stopCamera();
    };
  }, [stopCamera]);

  const handleBack = () => {
    stopCamera();
    navigate('/questionnaire');
  };

  return (
    <div className="camera-page">
      <div className="camera-container">
        <div className="camera-header">
          <button className="back-button" onClick={handleBack}>
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M15 18L9 12L15 6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
          </button>
          <h2 className="camera-title">{t('camera.title')}</h2>
        </div>

        <div className="camera-viewport">
          <video
            ref={videoRef}
            autoPlay
            playsInline
            muted
            className="camera-video"
            style={{ 
              display: stream ? 'block' : 'none',
              width: '100%',
              height: '100%',
              objectFit: 'cover'
            }}
          />
          <canvas
            ref={canvasRef}
            className="camera-canvas"
            style={{ display: isCameraActive ? 'block' : 'none' }}
          />
          {capturedImage && (
            <img
              src={capturedImage}
              alt={t('camera.capturedAlt')}
              className="captured-image"
            />
          )}
          {!stream && !capturedImage && !isCameraActive && (
            <div className="camera-placeholder">
              <p>{t('camera.placeholder')}</p>
              <button 
                className="start-camera-button"
                onClick={() => startCamera()}
                style={{
                  marginTop: '16px',
                  padding: '12px 24px',
                  backgroundColor: '#2c3e50',
                  color: 'white',
                  border: 'none',
                  borderRadius: '8px',
                  fontSize: '16px',
                  fontWeight: '600',
                  cursor: 'pointer'
                }}
              >
                {t('camera.startCamera')}
              </button>
            </div>
          )}
        </div>

        <div className="camera-controls">
          {!capturedImage ? (
            <>
              <div className="face-status">
                {faceDetected ? (
                  <span className={`status-text ${faceClear ? 'status-good' : 'status-warning'}`}>
                    {faceClear ? t('camera.faceClear') : t('camera.facePosition')}
                  </span>
                ) : (
                  <span className="status-text status-error">{t('camera.noFace')}</span>
                )}
              </div>
              <button
                className={`capture-button ${faceClear ? 'capture-ready' : ''}`}
                onClick={capturePhoto}
                disabled={!faceClear || !isCameraActive}
              >
                {t('camera.capture')}
              </button>
            </>
          ) : (
            <>
              <button
                className="retake-button"
                onClick={() => {
                  setCapturedImage(null);
                  setUploadError(null);
                  setUploadProgress(0);
                  startCamera();
                }}
                disabled={isUploading}
              >
                {t('camera.retake')}
              </button>
              <button
                className="start-analysis-button"
                onClick={handleStartAnalysis}
                disabled={isUploading}
              >
                {isUploading ? t('camera.uploading') : t('camera.startAnalysis')}
              </button>
              {isUploading && (
                <div className="upload-progress">
                  <div className="upload-progress-bar">
                    <div
                      className="upload-progress-fill"
                      style={{ width: `${uploadProgress}%` }}
                    />
                  </div>
                  <div className="upload-progress-text">
                    {uploadProgress}%
                  </div>
                </div>
              )}
              {uploadError && (
                <div className="face-status" style={{ marginTop: 12 }}>
                  <span className="status-text status-error">{uploadError}</span>
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default CameraPage;
